import { useState } from 'react'
import { useApi } from '../../core/api/client'
import { useTokens, type TokenRow } from '../../core/auth/useTokens'
import { roleLabel } from '../../core/auth/roles'

// User Summary: list every credential (minted token) you hold and, on expand,
// fetch a role-aware summary of that user from the backend. Sales agents get the
// rich profile (identity, tier, Quality of Sales, stock limit, suspensions,
// cluster, shop, held stock); staff get their basic details + managed shops.

interface StockListItem { serialNumber: string; modelName: string; brandName: string; ageLabel: string | null; receivedAt: string | null }

interface AgentSummary {
  kind: 'agent'
  role: string | null
  identity: { id: string; fullName: string; phoneNumber: string; countryIsoCode: string; status: string; isClusterLeader: boolean | null }
  tierId: string | null
  createdAt: string | null
  qos: { id: string; name: string; level: number | null; score: number | null } | null
  salesSummary: { salesThisWeek: number | null; salesThisMonth: number | null; lastSaleDate: string | null } | null
  stockLimit: { maxStock: number | null; allocatedStock: number | null; maxPossibleStock: number | null; message: string } | null
  suspensions: Array<{ id: string; action: string; reason: string; startedAt: string }>
  cluster: { id: string; name: string } | null
  shop: { id: string; name: string } | null
  possibleActions: string[]
  stock: { count: number; items: StockListItem[] } | null
  raw: unknown
}

interface StaffSummary {
  kind: 'staff'
  role: string | null
  identity: { staffId: string; fullName: string; phoneNumber: string; gender: string; countryIsoCode: string; status: string; statusReason: string | null; isClusterManager: boolean | null }
  shops: Array<{ id: string; name: string; shortName: string }> | null
  raw: unknown
}

type UserSummary = AgentSummary | StaffSummary

type Loaded = { state: 'loading' } | { state: 'error'; error: string } | { state: 'ok'; data: UserSummary }

export function SummaryView() {
  const api = useApi()
  const { tokens } = useTokens()

  const [openId, setOpenId] = useState<string | null>(null)
  const [byToken, setByToken] = useState<Record<string, Loaded>>({})
  const [showRaw, setShowRaw] = useState<Record<string, boolean>>({})

  async function load(t: TokenRow) {
    setByToken((m) => ({ ...m, [t.id]: { state: 'loading' } }))
    try {
      const data = await api.get<UserSummary>(`/m/summary/profile?tokenId=${t.id}`)
      setByToken((m) => ({ ...m, [t.id]: { state: 'ok', data } }))
    } catch (e) {
      setByToken((m) => ({ ...m, [t.id]: { state: 'error', error: e instanceof Error ? e.message : String(e) } }))
    }
  }

  function toggle(t: TokenRow) {
    if (openId === t.id) { setOpenId(null); return }
    setOpenId(t.id)
    // Expired tokens still load — the backend auto-refreshes them on use. If a
    // refresh genuinely can't happen, load() surfaces the error inline with Retry.
    if (!byToken[t.id]) void load(t)
  }

  return (
    <div className="module">
      <h2>User Summary</h2>
      <p className="muted">Everything the backend knows about each credential you hold. Expand a credential to load its summary.</p>

      {tokens.length === 0 ? (
        <p className="muted">No tokens yet — mint one in <strong>Token Minter</strong> first.</p>
      ) : (
        <ul className="reg-list">
          {tokens.map((t) => {
            const open = openId === t.id
            const loaded = byToken[t.id]
            return (
              <li key={t.id} className="reg">
                <button className="reg-head" onClick={() => toggle(t)} aria-expanded={open}>
                  <span className="reg-caret">{open ? '▾' : '▸'}</span>
                  <span className="reg-name">{t.friendly_name}</span>
                  <span className="reg-phone">{roleLabel(t.role)} · {t.environment}</span>
                  <span className="reg-pct">
                    <span className={`tag ${t.expired ? 'expired' : 'live'}`}>{t.expired ? 'expired' : 'live'}</span>
                  </span>
                </button>
                {open && (
                  <div className="reg-detail">
                    {loaded?.state === 'loading' && <p className="muted">Loading summary…</p>}
                    {loaded?.state === 'error' && (
                      <>
                        <div className="banner error">{loaded.error}</div>
                        <button className="ghost" onClick={() => void load(t)}>Retry</button>
                      </>
                    )}
                    {loaded?.state === 'ok' && (
                      <>
                        {loaded.data.kind === 'agent'
                          ? <AgentCard s={loaded.data} />
                          : <StaffCard s={loaded.data} />}
                        <div className="row" style={{ gap: 8, marginTop: 12 }}>
                          <button className="linklike" onClick={() => setShowRaw((m) => ({ ...m, [t.id]: !m[t.id] }))}>
                            {showRaw[t.id] ? 'hide raw JSON' : 'show raw JSON'}
                          </button>
                          <button className="linklike" onClick={() => void load(t)}>reload</button>
                        </div>
                        {showRaw[t.id] && <pre className="raw-json">{JSON.stringify(loaded.data.raw, null, 2)}</pre>}
                      </>
                    )}
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="sum-field">
      <span className="sum-label">{label}</span>
      <span className="sum-value">{children}</span>
    </div>
  )
}

const dash = (v: unknown) => (v === null || v === undefined || v === '' ? '—' : String(v))
const ratio = (a: number | null, b: number | null) => `${a ?? '?'} / ${b ?? '?'}`

function AgentCard({ s }: { s: AgentSummary }) {
  return (
    <div className="sum-card">
      <section>
        <h3>Identity</h3>
        <div className="sum-grid">
          <Field label="Name">{dash(s.identity.fullName)}</Field>
          <Field label="Status"><span className="status-pill on">{dash(s.identity.status)}</span></Field>
          <Field label="Phone">{dash(s.identity.phoneNumber)}</Field>
          <Field label="Country">{dash(s.identity.countryIsoCode)}</Field>
          <Field label="Agent id"><code>{dash(s.identity.id)}</code></Field>
          <Field label="Cluster leader">{s.identity.isClusterLeader === null ? '—' : s.identity.isClusterLeader ? 'yes' : 'no'}</Field>
          {s.cluster && <Field label="Cluster">{dash(s.cluster.name)}</Field>}
          {s.shop && <Field label="Shop">{dash(s.shop.name)}</Field>}
          <Field label="Tier">{dash(s.tierId)}</Field>
        </div>
      </section>

      <section>
        <h3>Quality of Sales</h3>
        {s.qos ? (
          <div className="sum-grid">
            <Field label="Grade">{dash(s.qos.name)}</Field>
            <Field label="Level">{dash(s.qos.level)}</Field>
            <Field label="Score">{dash(s.qos.score)}</Field>
          </div>
        ) : <p className="muted">No Quality of Sales data.</p>}
      </section>

      <section>
        <h3>Sales</h3>
        {s.salesSummary ? (
          <div className="sum-grid">
            <Field label="This week">{dash(s.salesSummary.salesThisWeek)}</Field>
            <Field label="This month">{dash(s.salesSummary.salesThisMonth)}</Field>
            <Field label="Last sale">{dash(s.salesSummary.lastSaleDate)}</Field>
          </div>
        ) : <p className="muted">No sales summary.</p>}
      </section>

      <section>
        <h3>Stock limit</h3>
        {s.stockLimit ? (
          <div className="sum-grid">
            <Field label="Allocated / max">{ratio(s.stockLimit.allocatedStock, s.stockLimit.maxStock)}</Field>
            <Field label="Max possible">{dash(s.stockLimit.maxPossibleStock)}</Field>
            {s.stockLimit.message && <Field label="Note">{s.stockLimit.message}</Field>}
          </div>
        ) : <p className="muted">No stock limit data.</p>}
      </section>

      {s.suspensions.length > 0 && (
        <section>
          <h3>Suspensions</h3>
          <ul className="pick-list">
            {s.suspensions.map((su) => (
              <li key={su.id}>
                <div className="alloc-row">
                  <span className="tag expired">{dash(su.action)}</span>
                  <span className="muted">{dash(su.reason)}</span>
                  {su.startedAt && <span className="pick-meta">{su.startedAt.slice(0, 10)}</span>}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section>
        <h3>Held stock {s.stock ? <span className="muted">· {s.stock.count}</span> : null}</h3>
        {!s.stock ? <p className="muted">Stock list unavailable.</p>
          : s.stock.count === 0 ? <p className="muted">No stock on hand.</p>
          : (
            <ul className="pick-list">
              {s.stock.items.map((it) => (
                <li key={it.serialNumber}>
                  <div className="alloc-row">
                    <code>{it.serialNumber}</code>
                    <span className="muted">{[it.brandName, it.modelName].filter(Boolean).join(' ') || '—'}</span>
                    {it.ageLabel && <span className="tag expired">{it.ageLabel}</span>}
                    {it.receivedAt && <span className="pick-meta">{it.receivedAt.slice(0, 10)}</span>}
                  </div>
                </li>
              ))}
            </ul>
          )}
      </section>

      {s.possibleActions.length > 0 && (
        <section>
          <h3>Possible actions</h3>
          <div className="chip-pick">
            {s.possibleActions.map((a) => <span key={a} className="tag">{a}</span>)}
          </div>
        </section>
      )}
    </div>
  )
}

function StaffCard({ s }: { s: StaffSummary }) {
  return (
    <div className="sum-card">
      <section>
        <h3>Identity</h3>
        <div className="sum-grid">
          <Field label="Name">{dash(s.identity.fullName)}</Field>
          <Field label="Status"><span className="status-pill on">{dash(s.identity.status)}</span></Field>
          <Field label="Phone">{dash(s.identity.phoneNumber)}</Field>
          <Field label="Gender">{dash(s.identity.gender)}</Field>
          <Field label="Country">{dash(s.identity.countryIsoCode)}</Field>
          <Field label="Staff id"><code>{dash(s.identity.staffId)}</code></Field>
          <Field label="Cluster manager">{s.identity.isClusterManager === null ? '—' : s.identity.isClusterManager ? 'yes' : 'no'}</Field>
          {s.identity.statusReason && <Field label="Status reason">{s.identity.statusReason}</Field>}
        </div>
      </section>

      <section>
        <h3>Managed shops {s.shops ? <span className="muted">· {s.shops.length}</span> : null}</h3>
        {!s.shops ? <p className="muted">Shops unavailable.</p>
          : s.shops.length === 0 ? <p className="muted">No shops managed.</p>
          : (
            <div className="chip-pick">
              {s.shops.map((sh) => <span key={sh.id} className="tag">{sh.name}{sh.shortName ? ` · ${sh.shortName}` : ''}</span>)}
            </div>
          )}
      </section>
    </div>
  )
}
